// Mail realtime — the ONE multiplexed SSE stream (GET /core/mail/stream,
// MAIL-SERVER-STREAM-RELAY-1), the house shape of chat's subscribeRealtime:
// app-lifetime handle on `root._mailStream`, workspace-keyed (reused while
// the tenant matches, closed + reopened on drift), NEVER closed by the /mail
// page — the header drawer and the unread counts need it on every route.
//
// Server frames (sdk MailService.subscribeStream): `mail.snapshot`
// { accounts, unread } on open, then `mail.thread.upsert|delete`,
// `mail.account.status|progress`, `mail.outbox.sent|failed`, `mail.unread`
// — each the producer's REST-shaped row with { accountId, workspaceId }
// merged on top.
import { getSDK } from '@symbo-ls/workspace-shared/functions/sdk.js'
import { mailWorkspaceId } from './sameWorkspaceWriter.js'
import { applyMailThreadDelete, applyMailThreadUpsert, withMailNavUnread } from './mailUtils.js'

const _unreadPatch = (root, rows) => {
  const counts = { ...(root.mailUnreadCounts || {}) }
  const list = Array.isArray(rows) ? rows : rows ? [rows] : []
  for (const r of list) {
    if (r && r.accountId !== undefined) counts[String(r.accountId)] = Number(r.n || r.unread || 0)
  }
  return counts
}

const _patchAccount = (root, accountId, patch) => {
  const id = accountId ? String(accountId) : null
  if (!id) return null
  const list = root.mailAccounts || []
  const at = list.findIndex((a) => a && String(a.id) === id)
  if (at === -1) return null
  const copy = [...list]
  copy[at] = { ...copy[at], ...patch }
  return copy
}

// One event → one root write. Exported for the unit suite (pure given a
// root-like object with `update`).
export const applyMailStreamEvent = function (root, ev) {
  if (!root || !ev || !ev.type) return
  const type = ev.type
  if (type === 'mail.snapshot') {
    const patch = { mailUnreadCounts: _unreadPatch(root, ev.unread) }
    // The snapshot's accounts are listAccounts' own rows (ACL-filtered). The
    // /settings card may hold a fresher full read; the snapshot still wins on
    // boot because it is the first producer to run.
    if (Array.isArray(ev.accounts)) patch.mailAccounts = ev.accounts
    root.update(withMailNavUnread(root, patch))
    return
  }
  if (type === 'mail.thread.upsert') {
    const next = applyMailThreadUpsert(root.mailThreads, ev.thread || ev.row || ev)
    if (next !== root.mailThreads) root.update({ mailThreads: next })
    return
  }
  if (type === 'mail.thread.delete') {
    const id = ev.id || ev.threadId || (ev.thread && (ev.thread.id || ev.thread._id))
    const next = applyMailThreadDelete(root.mailThreads, id)
    const patch = {}
    if (next !== root.mailThreads) patch.mailThreads = next
    if (id && root.mailActiveThreadId === String(id)) patch.mailActiveThreadId = null
    if (Object.keys(patch).length) root.update(patch)
    return
  }
  if (type === 'mail.account.status') {
    const accounts = _patchAccount(root, ev.accountId, {
      status: ev.status || (ev.account && ev.account.status),
      sync: ev.sync || (ev.account && ev.account.sync)
    })
    if (accounts) root.update({ mailAccounts: accounts })
    return
  }
  if (type === 'mail.account.progress') {
    const list = root.mailAccounts || []
    const at = list.findIndex((a) => a && String(a.id) === String(ev.accountId))
    if (at === -1) return
    const copy = [...list]
    copy[at] = {
      ...copy[at],
      sync: {
        ...(copy[at].sync || {}),
        ...(ev.sync || {}),
        progress: ev.progress ?? (ev.sync && ev.sync.progress)
      }
    }
    root.update({ mailAccounts: copy })
    return
  }
  if (type === 'mail.unread') {
    root.update(
      withMailNavUnread(root, {
        mailUnreadCounts: _unreadPatch(root, ev.counts || ev.unread || ev)
      })
    )
    return
  }
  if (type === 'mail.outbox.sent' || type === 'mail.outbox.failed') {
    const row = ev.outbox || ev.row || null
    const id = row && (row.id || row._id)
    if (!id) return
    const list = root.mailOutbox || []
    const at = list.findIndex((o) => o && String(o.id || o._id) === String(id))
    const copy = [...list]
    if (at === -1) copy.unshift(row)
    else copy[at] = { ...copy[at], ...row }
    root.update({ mailOutbox: copy })
  }
}

// The stream row-apply is also reachable by name (openMailThread refreshes a
// row from GET /threads/:id through the very same reducer).
export const applyMailStreamThreadUpsert = function (row) {
  const root = this && this.state && this.state.root
  if (!root || !row) return
  const next = applyMailThreadUpsert(root.mailThreads, row)
  if (next !== root.mailThreads) root.update({ mailThreads: next })
}

export const subscribeMailStream = async function () {
  const root = this && this.state && this.state.root
  if (!root) return null
  const wsId = mailWorkspaceId(root)

  const existing = root._mailStream
  if (existing) {
    if (existing._wsId === wsId) return existing
    try {
      existing.unsubscribe?.()
    } catch (_) {}
    root.update({ _mailStream: null })
  }

  // CLAIM THE SLOT BEFORE THE FIRST AWAIT. `getService` suspends (see below),
  // and the page mount (pages/index.js `_initMailPage`) and the drawer
  // (functions/drawer.js `_kickDrawerData`) both call this in one tick: two
  // callers would otherwise pass the check above, open two EventSources, and
  // leave the second one unreachable for ever, because `_mailStream` holds
  // one handle. The unsubscribe is DEFERRED into the handle — it closes the
  // transport whenever it lands, the shape chat's `_openChatSse` uses. Every
  // failure branch RELEASES the slot, so a later call still retries.
  //
  // KEY THE SLOT BY `_wsId`, NEVER BY OBJECT IDENTITY. `root.update` stores a
  // DEEP COPY of a plain object and keeps only function properties by
  // reference (measured live in the shell, v3.14: `s.update({ x: o });
  // s.x === o` is `false`, `s.x.fn === o.fn` is `true`). So
  // `root._mailStream === handle` is false the instant it is written, and an
  // identity guard would drop every frame and never release the slot.
  let unsubscribe = null
  let cancelled = false
  const handle = {
    _wsId: wsId,
    unsubscribe: () => {
      cancelled = true
      try {
        if (typeof unsubscribe === 'function') unsubscribe()
      } catch (_) {}
      unsubscribe = null
    }
  }
  const held = () => {
    const cur = root._mailStream
    return !!cur && cur._wsId === wsId
  }
  const release = () => {
    if (held()) root.update({ _mailStream: null })
  }
  root.update({ _mailStream: handle })

  try {
    // `getSDK()` hands back the shell's init-await Proxy (packages/shared/
    // functions/sdkClient.js `wrapWithInitAwait`): every function property is
    // rewritten to `(...args) => initPromise.then(() => value.apply(...))`,
    // and `getService` is NOT in that wrapper's SYNC_PASS set. So
    // `getService('mail')` answers a PROMISE, and a bare
    // `typeof svc.subscribeStream` reads `undefined` on it FOREVER — the
    // guard below returned null on every call and the stream never opened
    // (measured on /mail: no `GET /core/mail/stream` request, no toast, no
    // console line, while `await sdk.getService('mail')` resolved to the real
    // MailService carrying subscribeStream). Awaiting is correct under BOTH
    // shapes — `await` on a plain value returns it — so this stays right if
    // the wrapper ever passes `getService` through SYNC_PASS.
    const sdk = getSDK()
    const svc = sdk && typeof sdk.getService === 'function' ? await sdk.getService('mail') : null
    if (!svc || typeof svc.subscribeStream !== 'function') {
      release()
      return null
    }
    const unsub = svc.subscribeStream({ workspaceId: wsId || undefined }, (ev) => {
      // Tenant guard: a frame from a previous workspace's stream (closed a
      // tick late) must never write into the new tenant's slices. A re-key
      // replaces the slot's `_wsId`, a close nulls the slot — both stop the
      // late frame here.
      if (!held()) return
      applyMailStreamEvent(root, ev)
    })
    if (cancelled) {
      try {
        unsub?.()
      } catch (_) {}
    } else unsubscribe = typeof unsub === 'function' ? unsub : null
  } catch (err) {
    console.warn('[mail-rt] SSE subscribe failed:', (err && err.message) || err)
    release()
    return null
  }

  return handle
}
